import { Component, Element, Event, EventEmitter, Host, Prop, h } from '@stencil/core';
import { AiIconType } from '../../utils/icon-types';

@Component({
  tag: 'biel-search-button',
  styleUrl: 'biel-search-button.css',
  shadow: true,
})
export class BielSearchButton {
  bielSearch: any;
  @Element() el: HTMLElement;

  // Define component properties
  @Prop() buttonStyle: string = 'default';
  @Prop() customFont: boolean = false;
  @Prop() hideCtrlK: boolean = false;
  @Prop() hideIcon: boolean = false;
  @Prop() aiIcon: AiIconType = 'biel';
  @Prop() hideAvatars: boolean = false;
  @Prop() hideDescriptions: boolean = false;

  // Define biel-search properties
  @Prop() hideAskAiButton: boolean = false;
  @Prop() hideFilters: boolean = true;
  @Prop() hideNavigationButtons: boolean = false;
  @Prop() modalPosition: string = 'top-center';
  @Prop() project: string = '';
  @Prop() stacked: boolean = false;
  @Prop() cleanTitles: boolean = false;

  // Define biel-search text properties
  @Prop() aiResultFragment: string = 'Ask AI to answer your question';
  @Prop() searchPlaceholder: string = 'Search or ask AI...';

  // Define biel-bot properties
  @Prop() apiKey: string = '';
  @Prop() apiUrl: string = '';
  @Prop() disableInput: boolean = false;
  @Prop() email: string = '';
  @Prop() expandModal: boolean = false;
  @Prop() hideCloseButton: boolean = false;
  @Prop() hideExpandButton: boolean = false;
  @Prop() hideRefreshButton: boolean = false;
  @Prop() hideSettingsButton: boolean = false;
  @Prop() hideConnectButton: boolean = false;
  @Prop() hideFeedback: boolean = false;
  @Prop() hideSources: boolean = false;
  @Prop() initialMessages: { text: string; sender: 'user' | 'ai'; feedback?: 0 | 1 }[] = [];
  @Prop() showTermsModal: boolean = false;
  @Prop() keepConversation: boolean = true;

  // Define biel-bot text properties
  @Prop() errorMessage403: string =
    'Oops! The request URL or API key is invalid or not authorized for this project.';
  @Prop() errorMessage404: string = 'Oops! We could not find the provided project ID.';
  @Prop() errorMessageDefault: string = 'Oops! Please try again later.';
  @Prop() footerText: string = 'AI answers may be inaccurate. Verify with official sources.';
  @Prop() headerTitle: string = 'Company Name AI';
  @Prop() inputPlaceholderText: string = 'Type your message';
  @Prop() sendButtonText: string = 'Send';
  @Prop() sourcesText: string = 'Sources';
  @Prop() suggestedQuestions: string = '';
  @Prop() suggestedQuestionsTitle: string = 'Suggested questions';
  @Prop() termsCheckboxText: string = 'I have read and agree to the Terms & Conditions.';
  @Prop() termsDescription: string =
    "Please review our <a href='https://biel.ai/terms' target='_blank' rel='noopener'>Terms & Conditions</a> before proceeding.";
  @Prop() termsTitle: string = 'Chatbot Terms & Conditions';
  @Prop() welcomeMessage: string = '';
  @Prop() refreshButtonText: string = 'Refresh';
  @Prop() expandButtonText: string = 'Expand';
  @Prop() collapseButtonText: string = 'Collapse';
  @Prop() closeButtonText: string = 'Close';
  @Prop() settingsButtonText: string = 'Settings';
  @Prop() hideTooltips: boolean = false;
  @Prop() assistantLabel: string = 'Assistant';
  @Prop() metadata: string = '';
  @Prop() mcpServerUrl: string = '';
  @Prop() connectButtonText: string = 'Connect';
  @Prop() thinkModeAutoText: string = 'Auto';
  @Prop() thinkModeFastText: string = 'Instant';
  @Prop() thinkModeThinkText: string = 'Think';
  @Prop() thinkModeAutoDescription: string = 'Balances speed and quality';
  @Prop() thinkModeFastDescription: string = 'Prioritizes speed';
  @Prop() thinkModeThinkDescription: string = 'Takes longer for better accuracy';
  @Prop() mcpUrlText: string = 'MCP URL';
  @Prop() mcpUrlDescription: string = 'Copy to clipboard';
  @Prop() mcpClaudeText: string = 'Claude Code';
  @Prop() mcpClaudeDescription: string = 'CLI command';
  @Prop() mcpCopilotText: string = 'Copilot (VS code)';
  @Prop() mcpCopilotDescription: string = 'Opens in a new tab';
  @Prop() mcpCursorText: string = 'Cursor';
  @Prop() mcpCursorDescription: string = 'Opens in a new tab';
  @Prop() mcpCopiedText: string = 'Copied!';
  @Prop() mcpUrlCopiedDescription: string = 'URL copied to clipboard';
  @Prop() mcpClaudeCopiedDescription: string = 'Command copied to clipboard';

  // Define component events

  @Event() bielSearchButtonClick: EventEmitter<void>;

  connectedCallback() {
    this.bielSearch = document.createElement('biel-search');
    const props = [
      'hideAskAiButton',
      'hideFilters',
      'hideNavigationButtons',
      'modalPosition',
      'project',
      'aiResultFragment',
      'searchPlaceholder',
      'apiKey',
      'apiUrl',
      'disableInput',
      'email',
      'expandModal',
      'hideCloseButton',
      'hideExpandButton',
      'hideRefreshButton',
      'hideSettingsButton',
      'hideConnectButton',
      'hideFeedback',
      'hideSources',
      'showTermsModal',
      'errorMessage403',
      'errorMessage404',
      'errorMessageDefault',
      'footerText',
      'headerTitle',
      'inputPlaceholderText',
      'sendButtonText',
      'sourcesText',
      'suggestedQuestions',
      'suggestedQuestionsTitle',
      'termsCheckboxText',
      'termsDescription',
      'termsTitle',
      'welcomeMessage',
      'refreshButtonText',
      'expandButtonText',
      'collapseButtonText',
      'closeButtonText',
      'settingsButtonText',
      'hideTooltips',
      'stacked',
      'aiIcon',
      'hideAvatars',
      'keepConversation',
      'cleanTitles',
      'hideDescriptions',
      'assistantLabel',
      'metadata',
      'mcpServerUrl',
      'connectButtonText',
      'thinkModeAutoText',
      'thinkModeFastText',
      'thinkModeThinkText',
      'thinkModeAutoDescription',
      'thinkModeFastDescription',
      'thinkModeThinkDescription',
      'mcpUrlText',
      'mcpUrlDescription',
      'mcpClaudeText',
      'mcpClaudeDescription',
      'mcpCopilotText',
      'mcpCopilotDescription',
      'mcpCursorText',
      'mcpCursorDescription',
      'mcpCopiedText',
      'mcpUrlCopiedDescription',
      'mcpClaudeCopiedDescription',
    ];

    props.forEach((prop) => {
      this.bielSearch[prop] = this[prop];
    });

    document.body.appendChild(this.bielSearch);
  }

  disconnectedCallback() {
    document.body.removeChild(this.bielSearch);
  }

  componentDidLoad() {
    if (!this.customFont) {
      this.loadInterFont();
    }
    if (!this.hideCtrlK) {
      window.addEventListener('keydown', this.handleKeyPress);
    }
  }

  loadInterFont() {
    const link = document.createElement('link');
    link.href = 'https://fonts.googleapis.com/css2?family=Inter:wght@400;500&display=swap';
    link.rel = 'stylesheet';
    document.head.appendChild(link);
  }

  showSearchModal() {
    this.bielSearch.showSearchModal = true;
    this.bielSearchButtonClick.emit();
  }

  handleKeyPress = (event: KeyboardEvent) => {
    if (event.ctrlKey && (event.key === 'k' || event.key === 'K')) {
      event.preventDefault();
      this.showSearchModal();
    }
  };

  render() {
    return (
      <Host>
        <a
          class={`biel-search-button-content biel-search-button-content--${this.buttonStyle}`}
          part="button"
          onClick={() => this.showSearchModal()}
        >
          {!this.hideIcon && (
            <span class="biel-search-button-content-icon" part="icon">
              <biel-icon type="search" width="20" height="20"></biel-icon>
            </span>
          )}
          <slot />
          {!this.hideCtrlK && (
            <span class="biel-search-button-content-ctrl-k" part="shortcut">
              CTRL+K
            </span>
          )}
        </a>
      </Host>
    );
  }
}
